// useFocusModeController groups all the focus-mode data management into one hook.
// FocusModeV2 stays readable while this hook handles querying, sorting, and task actions.

import { useState, useEffect, useCallback, useRef } from 'react';
import type { Location, NavigateFunction } from 'react-router-dom';
import { toast } from 'sonner';
import { db, sql, TaskWithContext, TimeEntry } from '../../lib/db';
import { fetchFocusQueue } from './fetchFocusQueue';
import { ensureNextRegularTaskInstance } from './regularTaskScheduler';
import { getStoredNote, upsertStoredNote, clearStoredNote } from './notesCache';

interface FocusModeControllerOptions {
  dbUserId?: string;
  navigate: NavigateFunction;
  location: Location;
  isTimerRunning: boolean;
  timerTaskId?: string | null;
  startTimer: (taskId: string, taskTitle: string) => void;
  stopTimer: () => number;
  forceClearTimer: () => void;
}

interface FocusModeControllerResult {
  loading: boolean;
  taskQueue: TaskWithContext[];
  currentTask: TaskWithContext | null;
  notes: string;
  setNotes: (value: string) => void;
  recentTimeEntries: TimeEntry[];
  completingTask: boolean;
  isEditingSOP: boolean;
  startSopEditing: () => void;
  cancelSopEditing: () => void;
  editedSOP: string;
  setEditedSOP: (value: string) => void;
  handleLoadTimer: () => void;
  handleStopTimer: () => Promise<void>;
  handleCompleteTask: () => Promise<void>;
  handleBumpTask: () => void;
  handleDeferToTomorrow: () => Promise<void>;
  handleSaveSOP: () => Promise<void>;
}

export function useFocusModeController({
  dbUserId,
  navigate,
  location,
  isTimerRunning,
  timerTaskId,
  startTimer,
  stopTimer,
  forceClearTimer
}: FocusModeControllerOptions): FocusModeControllerResult {
  const [loading, setLoading] = useState(true);
  const [currentTask, setCurrentTask] = useState<TaskWithContext | null>(null);
  const [taskQueue, setTaskQueue] = useState<TaskWithContext[]>([]);
  const [notes, setNotes] = useState('');
  const [completingTask, setCompletingTask] = useState(false);
  const [completedTaskIds, setCompletedTaskIds] = useState<Set<string>>(new Set());
  const [isEditingSOP, setIsEditingSOP] = useState(false);
  const [editedSOP, setEditedSOP] = useState('');
  const [recentTimeEntries, setRecentTimeEntries] = useState<TimeEntry[]>([]);
  const isIntentionalSwitch = useRef(false);
const latestLoadRequest = useRef(0);
const latestEntriesRequest = useRef(0);
const lastNotesTaskIdRef = useRef<string | null>(null);
const suppressAutoStartRef = useRef(false);

  const loadTaskQueue = useCallback(async (forceUpdateCurrent = false, specificTaskId?: string) => {
    if (!dbUserId) return;

    const requestId = latestLoadRequest.current + 1;
    latestLoadRequest.current = requestId;
    setLoading(true);
    try {
      const enrichedQueue = await fetchFocusQueue(dbUserId, completedTaskIds);

      if (latestLoadRequest.current !== requestId) {
        return;
      }

      setTaskQueue(enrichedQueue);

      setCurrentTask(prevTask => {
        const preferredTaskId = specificTaskId || (isTimerRunning && timerTaskId ? timerTaskId : undefined);

        if (preferredTaskId) {
          const preferredTask = enrichedQueue.find(task => task.id === preferredTaskId);
          if (preferredTask) {
            return preferredTask;
          }
        }

        if (!forceUpdateCurrent && isIntentionalSwitch.current && prevTask) {
          const stillExistsForIntentionalSwitch = enrichedQueue.find(task => task.id === prevTask.id);
          if (stillExistsForIntentionalSwitch) {
            return stillExistsForIntentionalSwitch;
          }
          return prevTask;
        }

        if (forceUpdateCurrent || !prevTask) {
          return enrichedQueue[0] || null;
        }

        const stillExists = enrichedQueue.find(task => task.id === prevTask.id);
        if (stillExists) {
          return stillExists;
        }

        return enrichedQueue[0] || null;
      });

      if (isIntentionalSwitch.current && !forceUpdateCurrent) {
        isIntentionalSwitch.current = false;
      }
    } catch (error) {
      console.error('Error loading task queue:', error);
      toast.error('Failed to load tasks');
    } finally {
      if (latestLoadRequest.current === requestId) {
        setLoading(false);
      }
    }
  }, [dbUserId, completedTaskIds, isTimerRunning, timerTaskId]);

  useEffect(() => {
    if (!dbUserId) {
      return;
    }

    let isCancelled = false;

    const cleanupThenLoad = async () => {
      const loadTaskId = location.state?.loadTaskId as string | undefined;

      try {
        const cleanedCount = await db.tasks.cleanupDeferredTasks(dbUserId);
        if (cleanedCount > 0) {
          console.log(`Cleaned up ${cleanedCount} deferred tasks from previous days`);
        }
      } catch (err) {
        console.error('Error cleaning up deferred tasks:', err);
      } finally {
        if (!isCancelled) {
          // Wait for the cleanup to finish before building the queue so revived tasks show immediately.
          await loadTaskQueue(false, loadTaskId);
        }
      }
    };

    cleanupThenLoad();

    return () => {
      isCancelled = true;
    };
  }, [dbUserId, loadTaskQueue, location.state]);

  useEffect(() => {
    if (!dbUserId || !timerTaskId) return;
    loadTaskQueue(false, timerTaskId);
  }, [dbUserId, timerTaskId, loadTaskQueue]);

  useEffect(() => {
    if (!dbUserId || !taskQueue.length) return;

    const pendingLoadTaskId = location.state?.loadTaskId;
    if (pendingLoadTaskId) {
      navigate('/focus', { replace: true });
    }

    if (isIntentionalSwitch.current) {
      isIntentionalSwitch.current = false;
    }
  }, [dbUserId, taskQueue.length, location.state, navigate]);

  useEffect(() => {
    if (!dbUserId) {
      return;
    }

    // If no task is available, make sure any lingering timer is stopped and exit early
    if (!currentTask) {
      if (isTimerRunning) {
        stopTimer();
      }
      forceClearTimer();
      setRecentTimeEntries([]);
      lastNotesTaskIdRef.current = null;
      suppressAutoStartRef.current = false;
      return;
    }

    if (lastNotesTaskIdRef.current !== currentTask.id) {
      const storedNote = getStoredNote(dbUserId, currentTask.id);
      setNotes(storedNote ?? '');
      lastNotesTaskIdRef.current = currentTask.id;
    }

    setEditedSOP(currentTask.sop || '');
    setIsEditingSOP(false);

    // Some tasks (like future-dated monthly items) arrive inactive; don't auto-start timers for those
    if (currentTask.is_live === false) {
      if (isTimerRunning) {
        stopTimer();
      }
      return;
    }

    if (isTimerRunning && timerTaskId !== currentTask.id) {
      stopTimer();
    }

    if (!isTimerRunning || timerTaskId !== currentTask.id) {
      if (suppressAutoStartRef.current) {
        return;
      }
      startTimer(currentTask.id, currentTask.title);
    } else if (isTimerRunning) {
      suppressAutoStartRef.current = false;
    }
  }, [currentTask, dbUserId, forceClearTimer, isTimerRunning, startTimer, stopTimer, timerTaskId]);

  const loadTimeEntries = useCallback(async (taskId: string) => {
    if (!dbUserId) return;

    const requestId = latestEntriesRequest.current + 1;
    latestEntriesRequest.current = requestId;

    try {
      const entries = await db.timeEntries.findByTaskId(taskId, dbUserId);
      if (latestEntriesRequest.current !== requestId) {
        return;
      }
      setRecentTimeEntries(entries);
    } catch (error) {
      console.error('Error loading time entries for focus mode:', error);
    }
  }, [dbUserId]);

  useEffect(() => {
    if (!dbUserId || !currentTask) {
      return;
    }
    loadTimeEntries(currentTask.id);
  }, [dbUserId, currentTask?.id, loadTimeEntries]);

  useEffect(() => {
    if (!dbUserId || !currentTask) {
      return;
    }
    upsertStoredNote(dbUserId, currentTask.id, notes);
  }, [dbUserId, currentTask?.id, notes]);

  const handleLoadTimer = useCallback(() => {
    if (currentTask && !isTimerRunning) {
      startTimer(currentTask.id, currentTask.title);
    }
  }, [currentTask, isTimerRunning, startTimer]);

  const handleCompleteTask = useCallback(async () => {
    if (!currentTask || !dbUserId) return;

    setCompletingTask(true);
    try {
      let sessionMinutes = 0;
      if (isTimerRunning && timerTaskId === currentTask.id) {
        const elapsedMs = stopTimer();
        sessionMinutes = Math.max(1, Math.ceil(elapsedMs / 60000));

        if (elapsedMs > 0) {
          await db.timeEntries.create({
            task_id: currentTask.id,
            user_id: dbUserId,
            start_time: new Date(Date.now() - elapsedMs),
            end_time: new Date(),
            notes: notes || 'Task completed',
            break_minutes: 0
          });

          await loadTimeEntries(currentTask.id);
        }
      }

      const totalTrackedMinutes = await db.timeEntries.getTotalMinutesForTask(currentTask.id);

      if (currentTask.is_regular) {
        await db.tasks.update(currentTask.id, dbUserId, {
          status: 'Complete' as const,
          end_date: new Date(),
          time_spent: totalTrackedMinutes > 0 ? totalTrackedMinutes : undefined,
          time_spent_minutes: totalTrackedMinutes > 0 ? totalTrackedMinutes : undefined
        });

        await db.regularTaskHistory.recordCompletion(currentTask.id, dbUserId);
        await ensureNextRegularTaskInstance(currentTask, dbUserId);
      } else {
        await db.tasks.update(currentTask.id, dbUserId, {
          status: 'Complete' as const,
          end_date: new Date(),
          time_spent: totalTrackedMinutes > 0 ? totalTrackedMinutes : undefined,
          time_spent_minutes: totalTrackedMinutes > 0 ? totalTrackedMinutes : undefined
        });
      }

      await db.taskCompletionStatistics.recordCompletion({
        userId: dbUserId,
        taskId: currentTask.id,
        taskTitle: currentTask.title,
        taskDescription: currentTask.description,
        priority: currentTask.priority,
        minutesSpent: totalTrackedMinutes
      });

      const completionMessage = totalTrackedMinutes > 0
        ? `Task completed!${sessionMinutes > 0 ? ` ${sessionMinutes} minute${sessionMinutes === 1 ? '' : 's'} added this session.` : ''} Total recorded time: ${totalTrackedMinutes} minute${totalTrackedMinutes === 1 ? '' : 's'}.`
        : 'Task completed!';
      toast.success(completionMessage);
      clearStoredNote(dbUserId, currentTask.id);
      setCompletedTaskIds(prev => new Set(prev).add(currentTask.id));
      isIntentionalSwitch.current = true;

      const remainingTasks = taskQueue.filter(t => t.id !== currentTask.id);
      setTaskQueue(remainingTasks);
      setCurrentTask(remainingTasks[0] || null);
      setNotes('');
      forceClearTimer();

      try {
        await loadTaskQueue(true);
      } finally {
        setCompletedTaskIds(new Set());
      }
    } catch (error) {
      console.error('Error completing task:', error);
      toast.error('Failed to complete task');
    } finally {
      setCompletingTask(false);
    }
  }, [currentTask, dbUserId, forceClearTimer, isTimerRunning, timerTaskId, stopTimer, notes, taskQueue, loadTaskQueue, loadTimeEntries]);

  const handleBumpTask = useCallback(() => {
    if (currentTask && taskQueue.length > 1) {
      isIntentionalSwitch.current = true;

      if (isTimerRunning && timerTaskId === currentTask.id) {
        if (notes.trim()) {
          const elapsedMs = stopTimer();
          if (elapsedMs > 0) {
            db.timeEntries.create({
              task_id: currentTask.id,
              user_id: dbUserId!,
              start_time: new Date(Date.now() - elapsedMs),
              end_time: new Date(),
              notes,
              break_minutes: 0
            }).catch(err => {
              console.error('Error saving time entry when bumping task:', err);
            });
          }
        } else {
          stopTimer();
        }
      }

      const newQueue = taskQueue.filter(t => t.id !== currentTask.id);
      newQueue.push(currentTask);
      setTaskQueue(newQueue);
      setCurrentTask(newQueue[0]);
      setNotes('');
      forceClearTimer();
    }
  }, [currentTask, taskQueue, forceClearTimer, isTimerRunning, timerTaskId, notes, stopTimer, dbUserId]);

  const handleDeferToTomorrow = useCallback(async () => {
    if (!currentTask || !dbUserId) return;

    try {
      let elapsedMs = 0;
      const timerNotes = notes || 'Task deferred to tomorrow';

      if (isTimerRunning && timerTaskId === currentTask.id) {
        elapsedMs = stopTimer();

        if (elapsedMs > 0) {
          const elapsedMinutes = Math.floor(elapsedMs / 60000);
          await db.timeEntries.create({
            task_id: currentTask.id,
            user_id: dbUserId,
            start_time: new Date(Date.now() - elapsedMs),
            end_time: new Date(),
            notes: timerNotes,
            break_minutes: 0
          });
          await loadTimeEntries(currentTask.id);
        }
      } else if (notes.trim()) {
        await db.timeEntries.create({
          task_id: currentTask.id,
          user_id: dbUserId,
          start_time: new Date(),
          end_time: new Date(),
          notes: notes,
          break_minutes: 0
        });

        await loadTimeEntries(currentTask.id);
      }

      const totalTrackedMinutes = await db.timeEntries.getTotalMinutesForTask(currentTask.id);

      const deferredNotes = currentTask.notes?.includes('[Deferred to Tomorrow]')
        ? currentTask.notes
        : currentTask.notes
          ? `${currentTask.notes}\n[Deferred to Tomorrow]`
          : '[Deferred to Tomorrow]';

      await db.tasks.deferToTomorrow(
        currentTask.id,
        dbUserId,
        deferredNotes,
        totalTrackedMinutes > 0 ? totalTrackedMinutes : undefined
      );

      toast.success('Task deferred to tomorrow');
      clearStoredNote(dbUserId, currentTask.id);

      isIntentionalSwitch.current = true;

      const remainingTasks = taskQueue.filter(t => t.id !== currentTask.id);
      setTaskQueue(remainingTasks);
      setCurrentTask(remainingTasks[0] || null);
      setNotes('');
      forceClearTimer();

      try {
        await loadTaskQueue(true);
      } finally {
        setCompletedTaskIds(new Set());
      }
    } catch (error) {
      console.error('Error deferring task:', error);
      toast.error('Failed to defer task');
    }
  }, [currentTask, dbUserId, forceClearTimer, isTimerRunning, timerTaskId, stopTimer, notes, taskQueue, loadTaskQueue, loadTimeEntries]);

  const handleStopTimer = useCallback(async () => {
    if (!currentTask || !dbUserId || !isTimerRunning) return;

    if (!notes.trim()) {
      toast.error('Please add notes before stopping the timer');
      return;
    }

    try {
      suppressAutoStartRef.current = true;
      const elapsedMs = stopTimer();

      if (elapsedMs > 0) {
        const elapsedMinutes = Math.max(1, Math.floor(elapsedMs / 60000));
        await db.timeEntries.create({
          task_id: currentTask.id,
          user_id: dbUserId,
          start_time: new Date(Date.now() - elapsedMs),
          end_time: new Date(),
          notes: notes,
          break_minutes: 0
        });
        const totalTrackedMinutes = await db.timeEntries.getTotalMinutesForTask(currentTask.id);
        await db.tasks.update(currentTask.id, dbUserId, {
          time_spent: totalTrackedMinutes > 0 ? totalTrackedMinutes : undefined,
          time_spent_minutes: totalTrackedMinutes > 0 ? totalTrackedMinutes : undefined
        });

        const stopMessage = `Timer stopped. ${elapsedMinutes} minute${elapsedMinutes > 1 ? 's' : ''} added. Total recorded: ${totalTrackedMinutes} minute${totalTrackedMinutes === 1 ? '' : 's'}.`;
        toast.success(stopMessage);
        setNotes('');
        clearStoredNote(dbUserId, currentTask.id);
        await loadTimeEntries(currentTask.id);
        forceClearTimer();
      }

      navigate('/overview', { replace: true });
    } catch (error) {
      console.error('Error stopping timer:', error);
      toast.error('Failed to save time entry');
    }
  }, [currentTask, dbUserId, forceClearTimer, isTimerRunning, navigate, stopTimer, notes, loadTimeEntries]);

  const handleSaveSOP = useCallback(async () => {
    if (!currentTask || !dbUserId) return;

    try {
      await db.tasks.update(currentTask.id, dbUserId, { sop: editedSOP });
      setCurrentTask({ ...currentTask, sop: editedSOP });
      setIsEditingSOP(false);
      toast.success('SOP saved');
    } catch (error) {
      console.error('Error saving SOP:', error);
      toast.error('Failed to save SOP');
    }
  }, [currentTask, dbUserId, editedSOP]);

  const startSopEditing = useCallback(() => {
    setIsEditingSOP(true);
  }, []);

  const cancelSopEditing = useCallback(() => {
    if (currentTask) {
      setEditedSOP(currentTask.sop || '');
    }
    setIsEditingSOP(false);
  }, [currentTask]);

  return {
    loading,
    taskQueue,
    currentTask,
    notes,
    setNotes,
    recentTimeEntries,
    completingTask,
    isEditingSOP,
    startSopEditing,
    cancelSopEditing,
    editedSOP,
    setEditedSOP,
    handleLoadTimer,
    handleStopTimer,
    handleCompleteTask,
    handleBumpTask,
    handleDeferToTomorrow,
    handleSaveSOP
  };
}
